import { find, orderBy, toPairs } from 'es-toolkit/compat';
import {
  TockProductLevel,
  TockProductLevelValue,
  TockProductType,
} from '../types/productFeatures';

import { Props as ProductFeatureRestrictionProps } from '../components/product/ProductFeatureRestriction';

import { core, toEnumString } from '@tock/coretypes';
import { createContext } from 'react';

import * as React from 'react';
import protos from 'utils/protos';

const { SubscriptionAddonType } = protos;

export interface IProductFeatureContext {
  productType?: TockProductType;
  productLevel?: TockProductLevel;
  subscriptionAddons?: core.SubscriptionAddonType[];
}

export const ProductFeatureContext = createContext<IProductFeatureContext>({
  productType: undefined,
  productLevel: undefined,
  subscriptionAddons: undefined,
});

function getMinProductLevel(): TockProductLevel {
  const pairs = toPairs(TockProductLevelValue) as [TockProductLevel, number][];
  return orderBy(pairs, 1)[0][0];
}

export function customerMeetsRestrictions(
  {
    productLevel: customerProductLevel,
    productType: customerProductType,
    subscriptionAddons,
  }: IProductFeatureContext,
  {
    onlyForProductType,
    onlyForSubscriptionAddon,
    minEventsLevel = TockProductLevel.ESSENTIAL,
    maxEventsLevel = TockProductLevel.PREMIUM,
    minClassicLevel = TockProductLevel.INTRO,
    maxClassicLevel = TockProductLevel.PRO,
  }: ProductFeatureRestrictionProps
) {
  if (!customerProductType || (onlyForProductType && customerProductType !== onlyForProductType)) {
    return false;
  }
  if (onlyForSubscriptionAddon) {
    const requiredAddon = toEnumString(SubscriptionAddonType, onlyForSubscriptionAddon);
    return !!find(
      subscriptionAddons,
      (addon) => toEnumString(SubscriptionAddonType, addon) === requiredAddon
    );
  }

  const effectiveCustomerProductLevel = customerProductLevel || getMinProductLevel();
  if (customerProductType === TockProductType.EVENTS && minEventsLevel && maxEventsLevel) {
    return (
      TockProductLevelValue[effectiveCustomerProductLevel] <=
        TockProductLevelValue[maxEventsLevel] &&
      TockProductLevelValue[effectiveCustomerProductLevel] >= TockProductLevelValue[minEventsLevel]
    );
  } else if (
    customerProductType === TockProductType.CLASSIC &&
    minClassicLevel &&
    maxClassicLevel
  ) {
    return (
      TockProductLevelValue[effectiveCustomerProductLevel] <=
        TockProductLevelValue[maxClassicLevel] &&
      TockProductLevelValue[effectiveCustomerProductLevel] >= TockProductLevelValue[minClassicLevel]
    );
  }
  return false;
}

export function useCustomerMeetsRestrictions(props: ProductFeatureRestrictionProps) {
  const context = React.useContext(ProductFeatureContext);
  const { productType: customerProductType } = context;
  if (!customerProductType) {
    return null;
  }

  return customerMeetsRestrictions(context, props);
}
